import { CommonModule } from '@angular/common';
import { Component, OnInit, inject } from '@angular/core';
import { RouterModule } from '@angular/router';
import { SeoService } from '../../../../core/services/seo.service';
import { PLAYBOOK, PLAYBOOK_GROUPS } from '../../../../shared/guides/guide.registry';
import { OfflineBannerComponent } from "../../../../shared/components/offline-banner/offline-banner";
import { PrepSignalGridComponent, PrepSignalItem } from '../../../../shared/components/prep-signal-grid/prep-signal-grid.component';

const PLAYBOOK_ENTRIES = new Map(PLAYBOOK.map((entry) => [entry.slug, entry]));
const PLAYBOOK_GLOBAL_INDEX = (() => {
  const map = new Map<string, number>();
  let n = 0;
  for (const group of PLAYBOOK_GROUPS) {
    for (const item of group.items) {
      map.set(item.slug, ++n);
    }
  }
  return map;
})();

@Component({
    imports: [CommonModule, RouterModule, OfflineBannerComponent, PrepSignalGridComponent],
    styles: [`
    :host { display:block; color: var(--uf-text-primary); background: var(--uf-bg); }
    .wrap { max-width: 980px; margin: 0 auto; padding: 24px clamp(18px, 4vw, 28px) 48px; }
    .hero-title { margin: 6px 0 6px; }
    .hero-sub { color: color-mix(in srgb, var(--uf-text-secondary) 88%, transparent); margin-bottom: 18px; }
    .pill-row { display:flex; gap:10px; flex-wrap:wrap; margin:12px 0 18px; }
    .pill { padding:6px 12px; }

    .prep-cluster {
      margin: 10px 0 24px;
      padding: 14px;
      border-radius: var(--uf-card-radius);
      border: 1px solid var(--uf-border-subtle);
      background: color-mix(in srgb, var(--uf-surface) 94%, var(--uf-surface-alt));
      box-shadow: var(--uf-card-shadow);
      display: grid;
      gap: 12px;
    }
    .prep-grid-shared { margin: 0; }
    .framework-links {
      display: flex;
      flex-wrap: wrap;
      gap: 8px;
    }
    .framework-links a {
      display: inline-flex;
      align-items: center;
      min-height: 30px;
      padding: 0 12px;
      border-radius: var(--uf-radius-pill);
      border: 1px solid var(--uf-border-subtle);
      background: color-mix(in srgb, var(--uf-surface) 90%, var(--uf-surface-alt));
      color: color-mix(in srgb, var(--uf-text-secondary) 84%, transparent);
      text-decoration: none;
      font-size: 12px;
      font-weight: 600;
      transition: border-color 140ms ease, color 140ms ease;
    }
    .framework-links a:hover {
      border-color: color-mix(in srgb, var(--uf-border-subtle) 60%, var(--uf-accent) 40%);
      background: color-mix(in srgb, var(--uf-accent) 12%, var(--uf-surface));
      color: var(--uf-text-primary);
    }

    .section { margin: 28px 0; }
    .sec-head { margin: 0 0 10px; color: var(--uf-text-primary); }

    .card {
      position: relative;
      isolation: isolate;
      overflow: hidden;
      contain: paint;
      display:flex; align-items:center; gap:14px;
      padding:14px 14px; text-decoration:none; color:inherit;
      background: var(--uf-surface);
      border: 1px solid var(--uf-border-subtle);
      border-radius: var(--uf-card-radius);
      box-shadow: var(--uf-card-shadow);
      transition: border-color 140ms ease, transform 120ms ease;
    }
    .card::before {
      content: "";
      position: absolute;
      inset: 0;
      pointer-events: none;
      background: color-mix(in srgb, var(--uf-surface) 86%, var(--uf-accent) 14%);
      opacity: 0;
      transition: opacity 140ms ease;
    }
    .card > * {
      position: relative;
      z-index: 1;
    }
    .card:hover {
      border-color: color-mix(in srgb, var(--uf-border-subtle) 60%, var(--uf-accent) 40%);
      transform: translateY(-1px);
    }
    .card:hover::before { opacity: 1; }
    .num { min-width:32px; height:32px; border-radius: var(--uf-radius-pill); display:grid; place-items:center; }
    .body { flex:1; min-width:0; }
    .title { margin:0; }
    .sub { white-space:nowrap; overflow:hidden; text-overflow:ellipsis; color: color-mix(in srgb, var(--uf-text-secondary) 80%, transparent); }
    .mins { font-size:12px; color: color-mix(in srgb, var(--uf-text-tertiary) 75%, transparent); }
    .arrow { color: color-mix(in srgb, var(--uf-text-tertiary) 75%, transparent); transition: transform 160ms ease, opacity 160ms ease; }
    .card:hover .arrow { opacity:1; transform: translateX(4px); color: var(--uf-text-primary); }

    @media (prefers-reduced-motion: reduce) {
      .card { transition: border-color 0.01ms linear; transform: none !important; }
      .card::before { transition: opacity 0.01ms linear; }
      .arrow { transition: opacity 0.01ms linear; transform: none !important; }
      .framework-links a { transition: border-color 0.01ms linear, color 0.01ms linear; }
    }
  `],
    template: `
    <div class="wrap fa-body">
      <h1 class="hero-title fa-page-title">Frontend Interview Playbook Hub</h1>
      <div class="hero-sub fa-meta-text">A practical hub for frontend interview preparation guides covering coding, UI, JavaScript, system design, framework paths, fundamentals, and resume prep.</div>
      <div class="hero-sub fa-meta-text">
        Need the full sequence first?
        <a [routerLink]="['/guides','interview-blueprint','intro']">Start with the Frontend interview preparation guide</a>.
      </div>
      <div class="pill-row">
        <span class="pill fa-chip fa-chip--label">Interview playbook hub</span>
        <span class="pill fa-chip fa-chip--label">Tips for all question types</span>
        <span class="pill fa-chip fa-chip--label">500+ practice questions</span>
      </div>

      <section class="prep-cluster" aria-label="Interview preparation path and outcomes">
        <app-prep-signal-grid
          class="prep-grid-shared"
          [outcomes]="prepOutcomes"
          [mistakes]="prepMistakes"
          [sequence]="prepSequence">
        </app-prep-signal-grid>

        <div class="framework-links">
          <a [routerLink]="['/guides/framework-prep']">Browse all framework prep paths</a>
          <a [routerLink]="['/guides/framework-prep/javascript-prep-path']">JavaScript path</a>
          <a [routerLink]="['/guides/framework-prep/react-prep-path']">React interview preparation path</a>
          <a [routerLink]="['/guides/framework-prep/angular-prep-path']">Angular path</a>
          <a [routerLink]="['/guides/framework-prep/vue-prep-path']">Vue path</a>
        </div>
      </section>

      <div *ngFor="let g of groups" class="section">
        <h2 class="sec-head fa-section-title">{{ g.title }}</h2>

        <a *ngFor="let it of g.items"
           class="card" [routerLink]="routeForSlug(it.slug)">
          <div class="num fa-chip fa-chip--label">{{ globalIdx.get(it.slug) }}</div>
          <div class="body">
            <div class="title fa-card-title">{{ entries.get(it.slug)?.title || it.slug }}</div>
            <div class="sub fa-meta-text">{{ entries.get(it.slug)?.summary }}</div>
          </div>
          <div class="mins" *ngIf="entries.get(it.slug)?.minutes as m">{{ m }} min</div>
          <div class="arrow" aria-hidden="true">→</div>
        </a>
        <p *ngIf="g.key === 'resume'" class="mt-3 text-[var(--uf-text-secondary)]">
          Already have a draft?
          <a routerLink="/tools/cv" class="text-[var(--uf-accent)] underline underline-offset-2">Review it with the CV Linter</a>.
        </p>
      </div>

       <app-offline-banner></app-offline-banner>
    </div>
  `
})
export class PlaybookIndexComponent implements OnInit {
  private readonly seo = inject(SeoService);
  private readonly canonicalPath = '/guides/interview-blueprint';
  readonly pageTitle = 'Frontend Interview Playbook Hub';
  readonly pageDescription = 'Use this frontend interview playbook hub to choose guides for coding, UI, JavaScript, system design, behavioral prep, framework paths, fundamentals, and resume prep.';

  readonly prepOutcomes: PrepSignalItem[] = [
    'Know exactly what to practice each week for frontend interview preparation.',
    'Move from random solving to a repeatable interview roadmap.',
    'Improve coding, UI, system design, and behavioral consistency.',
  ];

  readonly prepMistakes: PrepSignalItem[] = [
    'Over-focusing on trivia without implementation practice.',
    'Ignoring communication quality and trade-off reasoning.',
    'Switching topics daily instead of completing one prep loop.',
  ];

  readonly prepSequence: PrepSignalItem[] = [
    { text: 'Frontend interview preparation guide', route: ['/guides/interview-blueprint/intro'] },
    { text: 'Frontend coding interview questions and prep guide', route: ['/guides/interview-blueprint/coding-interviews'] },
    { text: 'UI rounds', route: ['/guides/interview-blueprint/ui-interviews'] },
    { text: 'System design rounds', route: ['/guides/interview-blueprint/system-design'] },
    { text: 'Behavioral interview blueprint', route: ['/guides/behavioral'] },
  ];

  readonly faqItems = [
    {
      question: 'What is the Frontend Interview Playbook Hub?',
      answer: 'It is the FrontendAtlas guide hub for choosing the right frontend interview preparation guide, coding round guide, UI interview guide, system design guide, framework path, fundamentals check, or resume prep resource.',
    },
    {
      question: 'Where should I start?',
      answer: 'Start with the Frontend interview preparation guide, then move into coding, UI, JavaScript, system design, and behavioral prep based on your weakest interview round.',
    },
    {
      question: 'Is this hub the same as the full preparation guide?',
      answer: 'No. This hub organizes the guide cluster. The full frontend interview preparation guide explains the rounds, roadmap, question categories, and next practice sequence in one article.',
    },
  ];

  ngOnInit(): void {
    this.publishSeo();
  }

  routeForSlug(slug: string): string[] {
    if (String(slug).endsWith('-prep-path')) {
      return ['/', 'guides', 'framework-prep', slug];
    }
    return ['/', 'guides', 'interview-blueprint', slug];
  }

  /** Section groups (order matters). */
  readonly groups = PLAYBOOK_GROUPS;

  /** Quick lookup: slug -> GuideEntry */
  readonly entries = PLAYBOOK_ENTRIES;

  /** Global numbering: slug -> 1..N across all sections */
  readonly globalIdx = PLAYBOOK_GLOBAL_INDEX;

  private publishSeo(): void {
    const canonicalUrl = this.seo.buildCanonicalUrl(this.canonicalPath);
    const itemList = {
      '@type': 'ItemList',
      '@id': `${canonicalUrl}#frontend-interview-playbook-guides`,
      name: 'Frontend interview playbook guide cluster',
      itemListElement: PLAYBOOK_GROUPS.flatMap((group) => group.items).map((item, index) => {
        const entry = PLAYBOOK_ENTRIES.get(item.slug);
        const path = this.pathForSlug(item.slug);
        return {
          '@type': 'ListItem',
          position: index + 1,
          name: entry?.title || item.slug,
          url: this.seo.buildCanonicalUrl(path),
        };
      }),
    };

    const collectionPage = {
      '@type': 'CollectionPage',
      '@id': canonicalUrl,
      url: canonicalUrl,
      name: this.pageTitle,
      description: this.pageDescription,
      inLanguage: 'en',
      about: [
        { '@type': 'Thing', name: 'frontend interview playbook hub' },
        { '@type': 'Thing', name: 'frontend interview preparation guides' },
        { '@type': 'Thing', name: 'frontend interview prep guide cluster' },
      ],
      mainEntity: itemList,
    };

    const breadcrumb = {
      '@type': 'BreadcrumbList',
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          name: 'FrontendAtlas',
          item: this.seo.buildCanonicalUrl('/'),
        },
        {
          '@type': 'ListItem',
          position: 2,
          name: 'Guides',
          item: this.seo.buildCanonicalUrl('/guides'),
        },
        {
          '@type': 'ListItem',
          position: 3,
          name: this.pageTitle,
          item: canonicalUrl,
        },
      ],
    };

    const faqPage = {
      '@type': 'FAQPage',
      '@id': `${canonicalUrl}#faq`,
      url: canonicalUrl,
      name: 'Frontend interview playbook hub FAQ',
      mainEntity: this.faqItems.map((item) => ({
        '@type': 'Question',
        name: item.question,
        acceptedAnswer: {
          '@type': 'Answer',
          text: item.answer,
        },
      })),
    };

    this.seo.updateTags({
      title: this.pageTitle,
      description: this.pageDescription,
      keywords: [
        'frontend interview playbook hub',
        'frontend interview preparation guides',
        'frontend interview prep guide cluster',
        'frontend coding interview guide',
        'frontend UI interview guide',
        'frontend system design interview guide',
        'JavaScript interview preparation guide',
      ],
      canonical: this.canonicalPath,
      jsonLd: [collectionPage, breadcrumb, itemList, faqPage],
    });
  }

  private pathForSlug(slug: string): string {
    if (String(slug).endsWith('-prep-path')) {
      return `/guides/framework-prep/${slug}`;
    }
    return `/guides/interview-blueprint/${slug}`;
  }
}
